"use client";

import React from "react";
import { listingService } from "../services";
import PriceSkeleton from "@/src/features/listing/components/skeletons/PriceSkeleton";
import { type IFilter } from "@/src/shared/features/search";
import { useListingPrice } from "@/src/shared/features/listing";
import useDateDifference from "@/src/shared/hooks/useDateDifference";

interface IPriceDisplayProps {
  variant?: "web" | "mobile";
  filters: IFilter;
}

const PriceDisplay: React.FC<IPriceDisplayProps> = ({
  variant = "web",
  filters,
}) => {
  const { loading, data } = useListingPrice(filters);
  const totalSelectedDays = useDateDifference({
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
  });
  const currency = "BDT";

  // Show skeleton while loading
  if (loading) {
    return <PriceSkeleton variant={variant} />;
  }

  if (variant === "mobile") {
    const totalPrice = data?.average_price
      ? data.average_price * totalSelectedDays
      : 0;
    const originalTotalPrice = data?.before_discount
      ? data.before_discount * totalSelectedDays
      : 0;
    const hasDiscount =
      data?.before_discount &&
      data.before_discount > (data?.average_price || 0);

    // The app's bar: no price until dates are picked, then the stay total and
    // "For 3 nights · 12–15 Oct".
    if (!totalSelectedDays || !filters.checkIn || !filters.checkOut) {
      return (
        <p className="text-base font-semibold leading-4 text-ink-secondary">
          Select date for price
        </p>
      );
    }

    return (
      <div className="flex min-w-0 flex-col">
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold leading-6 text-ink">
            ৳{totalPrice.toLocaleString("en-US")}
          </span>
          {hasDiscount && (
            <span className="text-xs text-ink-secondary line-through">
              ৳{originalTotalPrice.toLocaleString("en-US")}
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs leading-4 text-ink-secondary">
          For {totalSelectedDays} {totalSelectedDays === 1 ? "night" : "nights"}{" "}
          ·{" "}
          {listingService.stayDatesLabel(
            new Date(filters.checkIn),
            new Date(filters.checkOut),
          )}
        </p>
      </div>
    );
  }

  // Web variant (for main page)
  const hasDiscount =
    data?.before_discount && data.before_discount > (data?.average_price || 0);
  const totalPrice = data?.average_price
    ? data.average_price * totalSelectedDays
    : 0;

  // Skip showing total price when totalSelectedDays is null or 0
  if (!totalSelectedDays || totalSelectedDays === 0) {
    return (
      <div className="flex flex-col gap-3 pb-6">
        <div className="flex items-center gap-3">
          {hasDiscount && (
            <div className="flex items-center gap-2">
              <span className="text-lg text-ink-muted font-bold line-through">
                {currency}{" "}
                <span className="ml-1">
                  {" "}
                  {data.before_discount?.toLocaleString()}{" "}
                </span>
              </span>
              <span className="text-sm text-ink-muted">/day</span>
            </div>
          )}
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-coral-pink-500">
              {currency}{" "}
              <span className="ml-1">
                {" "}
                {data?.average_price?.toLocaleString()}{" "}
              </span>
            </span>
            <span className="text-base text-ink-gray font-medium">/day</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-6">
      <div className="flex items-center gap-3">
        {hasDiscount && (
          <div className="flex items-center gap-2">
            <span className="text-lg text-ink-muted font-bold line-through">
              {currency}{" "}
              <span className="ml-1">
                {" "}
                {data.before_discount?.toLocaleString()}
              </span>
            </span>
            <span className="text-sm text-ink-muted">/day</span>
          </div>
        )}
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-bold text-coral-pink-500">
            {currency}{" "}
            <span className="ml-1">
              {" "}
              {data?.average_price?.toLocaleString()}{" "}
            </span>
          </span>
          <span className="text-base text-ink-gray font-medium">/day</span>
        </div>
      </div>
      <div className="text-left">
        <div className="text-base font-medium text-ink-black">
          {currency}
          <span className="ml-1"> {totalPrice.toLocaleString()}</span>
          <span className="text-sm font-medium text-ink-secondary ml-2">
            for {totalSelectedDays} nights
          </span>
        </div>
      </div>
    </div>
  );
};

export default PriceDisplay;
